import { MutualFundTableBodyProps, TableBodyData } from "../../investorOverview/types";
import { Box, TableCell, TableRow, Typography, useTheme, Grid } from "@mui/material";
import { SxProps, Theme } from "@mui/system";
import { toINR } from "../../../utils/utilityFunctions";
import { TransactionDetailsData } from "../../../redux-store/types/products";
import { useSelector } from "react-redux";
import { RootStateType } from "../../../redux-store/reducers";

export default function MutualFundTranscationBody(
    {
        tableBodyData,
        tableBodyRowCustomStyles = {},
    }: {
        tableBodyData: TransactionDetailsData[];
        tableBodyRowCustomStyles: SxProps<Theme>,
    }
): JSX.Element {
    const { palette } = useTheme()

    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    return (
        <>
            {tableBodyData?.map((obj: TableBodyData) => (
                <TableRow  
                    key={obj?.scheme}
                    sx={{
                        '& td': {
                            paddingTop: "15px",
                            paddingBottom: "15px",
                            borderBottom: `0.5px solid ${palette.text.secondary}B2`,
                        },
                        ...tableBodyRowCustomStyles
                    }}
                >
                    <TableCell
                        align="left"
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexDirection: 'row' }}>

                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    color: theme.palette.text.primary,
                                    '&:hover': {
                                        color: theme.palette.primary[500]
                                    },
                                    lineHeight: "20px",
                                    letterSpacing: '0.02px'
                                })}
                            >
                                {obj?.schemeName}
                            </Typography>
                        </Box>
                    </TableCell>
                    <TableCell
                        align="center"
                    >
                        <Typography
                            variant="body_500"
                            sx={{
                                color: palette.text.primary,
                                lineHeight: "20px",
                                letterSpacing: "0.02px"
                            }}
                        >
                            {obj?.schemeCategory}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={{
                                color: palette.text.primary,
                                lineHeight: "20px",
                                letterSpacing: "0.02px"
                            }}
                        >
                            {toINR({ value: obj?.amount, locale, currencyCode })}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={{
                                color: palette.text.primary,
                                lineHeight: "20px",
                                letterSpacing: "0.02px"
                            }}
                        >
                            {!Number.isNaN(Number(obj?.units)) ?
                                Math.round(obj.units * 100) / 100 : '0'}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={{
                                color: palette.text.primary,
                                lineHeight: "20px",
                                letterSpacing: "0.02px"
                            }}
                        >

                            {obj?.type}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={{
                                color: palette.text.primary,
                                lineHeight: "20px",
                                letterSpacing: "0.02px"
                            }}
                        >

                            {obj?.date}
                        </Typography>
                    </TableCell>
                </TableRow>
            ))}
        </>
    )
}

export function MobileResponsiveBodyTransaction(
  {
    data,
    tableHeaderData
  }: {
    data: TransactionDetailsData[];
    tableHeaderData: Array<{ id: string; label: string }>;
  }
): JSX.Element {
  const { palette } = useTheme();
  const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

  const getLabel = (id: string, fallback: string) => tableHeaderData.find(h => (h as any).id === id)?.label || fallback;

  return (
    <>
      {data?.map((obj, index) => (
        <Box
          key={`${obj?.schemeName}-${index}`}
          sx={{
            border: `0.5px solid ${palette.text.secondary}40`,
            borderRadius: '12px',
            p: 2,
            mb: 2,
            backgroundColor: palette.background.paper
          }}
        >
          <Typography
            variant="body_600"
            sx={{ color: palette.text.primary, mb: 1 }}
          >
            {obj?.schemeName || '-'}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body_500" sx={{ color: palette.text.secondary, fontSize: '12px' }}>
                {getLabel('schemeCategory', 'Scheme Category')}
              </Typography>
              <Typography variant="body_500" sx={{ color: palette.text.primary }}>
                {obj?.schemeCategory || '-'}
              </Typography>
            </Grid>

            {/* Right side */}
            <Grid item xs={6} sx={{ textAlign: "end" }}>
              <Typography
                variant="body_500"
                sx={{ color: palette.text.secondary, fontSize: "12px" }}
              >
                {getLabel("amount", "Transaction Amount")}
              </Typography>
              <Typography variant="body_500" sx={{ color: palette.text.primary }}>
                {toINR({ value: obj?.amount, locale, currencyCode })}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body_500" sx={{ color: palette.text.secondary, fontSize: '12px' }}>
                {getLabel('units', 'Transaction Units')}
              </Typography>
              <Typography variant="body_500" sx={{ color: palette.text.primary }}>
                {!Number.isNaN(Number(obj?.units)) ? Math.round(Number(obj.units) * 100) / 100 : '0'}
              </Typography>
            </Grid>

            {/* Right side */}
            <Grid item xs={6} sx={{ textAlign: "end" }}>
              <Typography
                variant="body_500"
                sx={{ color: palette.text.secondary, fontSize: "12px" }}
              >
                {getLabel("type", "Transaction Type")}
              </Typography>
              <Typography variant="body_500" sx={{ color: palette.text.primary }}>
                {obj?.type || '-'}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body_500" sx={{ color: palette.text.secondary, fontSize: '12px' }}>
                {getLabel('date', 'Transaction Date')}
              </Typography>
              <Typography variant="body_500" sx={{ color: palette.text.primary }}>
                {obj?.date || '-'}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      ))}
    </>
  );
}